import { create } from "zustand";
import type {
  AppValidationError,
  EditorMode,
  SectionId,
  ViewportState,
} from "@/types/editor";
import { FALLBACK_STYLE } from "@/lib/defaultStyle";
import { MAP_DEFAULTS } from "@/styles/theme";
import { validateStyleSpec } from "@/lib/validation";

/** Typed layer for the store — avoids TS infinite depth with the full StyleSpecification. */
export interface StoreLayer {
  id: string;
  type: string;
  source?: string;
  "source-layer"?: string;
  minzoom?: number;
  maxzoom?: number;
  filter?: unknown;
  layout?: Record<string, unknown>;
  paint?: Record<string, unknown>;
  metadata?: unknown;
  [key: string]: unknown;
}

/** Loosely-typed style for the store to avoid TS infinite depth on StyleSpecification. */
export type Style = Record<string, unknown> & { layers: StoreLayer[]; sources?: Record<string, unknown> };

interface EditorState {
  style: Style;
  originalStyle: Style | null;
  showComparison: boolean;
  undoStack: string[];
  redoStack: string[];
  styleVersion: number;

  editorMode: EditorMode;
  activeSection: SectionId;
  selectedLayerId: string | null;
  selectedSourceId: string | null;
  hoveredLayerId: string | null;
  validationErrors: AppValidationError[];
  isErrorPanelOpen: boolean;

  viewport: ViewportState;
  highlightLayerId: string | null;
  /** Set by loadNewStyle — MapPreview consumes and clears this to fly to the style's initial view */
  pendingFlyTo: { center: [number, number]; zoom: number; pitch: number; bearing: number } | null;

  // Accept Style or StyleSpecification (from file load/URL fetch)
  setStyle: (style: Style | Record<string, unknown>, skipUndo?: boolean) => void;
  updateRoot: (key: string, value: unknown) => void;
  updateLayerPaint: (layerId: string, property: string, value: unknown) => void;
  updateLayerLayout: (layerId: string, property: string, value: unknown) => void;
  updateLayerCommon: (layerId: string, property: string, value: unknown) => void;
  addLayer: (layer: StoreLayer, position?: "top" | "bottom") => void;
  removeLayer: (layerId: string) => void;
  reorderLayer: (fromIndex: number, toIndex: number) => void;
  toggleLayerVisibility: (layerId: string) => void;
  addSource: (id: string, source: Record<string, unknown>) => void;
  removeSource: (id: string) => void;
  updateSource: (id: string, source: Record<string, unknown>) => void;

  undo: () => void;
  redo: () => void;

  setEditorMode: (mode: EditorMode) => void;
  setActiveSection: (section: SectionId) => void;
  setSelectedLayerId: (id: string | null) => void;
  setSelectedSourceId: (id: string | null) => void;
  setHoveredLayerId: (id: string | null) => void;
  setValidationErrors: (errors: AppValidationError[]) => void;
  toggleErrorPanel: () => void;

  setViewport: (viewport: Partial<ViewportState>) => void;
  setHighlightLayerId: (id: string | null) => void;
  toggleComparison: () => void;

  /** Loads a completely new style — resets history, comparison baseline, and dirty state. */
  loadNewStyle: (style: Style | Record<string, unknown>) => void;
  /** True if the style has been modified since last load/save. */
  hasUnsavedChanges: () => boolean;

  runValidation: () => void;
}

const MAX_UNDO = 50;

function cloneStyle(style: Style): Style {
  return JSON.parse(JSON.stringify(style)) as Style;
}

export const useEditorStore = create<EditorState>()((set, get) => ({
  style: FALLBACK_STYLE as unknown as Style,
  originalStyle: null,
  showComparison: true,
  undoStack: [],
  redoStack: [],
  styleVersion: 0,

  editorMode: "sections",
  activeSection: "layers",
  selectedLayerId: null,
  selectedSourceId: null,
  hoveredLayerId: null,
  validationErrors: [],
  isErrorPanelOpen: false,

  viewport: {
    zoom: MAP_DEFAULTS.zoom,
    center: [...MAP_DEFAULTS.center],
    bearing: MAP_DEFAULTS.bearing,
    pitch: MAP_DEFAULTS.pitch,
  },
  highlightLayerId: null,
  pendingFlyTo: null,

  setStyle: (style, skipUndo) => {
    const prev = get();
    const undoStack = skipUndo
      ? prev.undoStack
      : [...prev.undoStack.slice(-(MAX_UNDO - 1)), JSON.stringify(prev.style)];
    set({
      style: style as Style,
      // Store the first loaded style as the "original" for comparison
      originalStyle: prev.originalStyle ?? cloneStyle(style as Style),
      undoStack,
      redoStack: skipUndo ? prev.redoStack : [],
      styleVersion: prev.styleVersion + 1,
    });
  },

  updateRoot: (key, value) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    style[key] = value;
    get().setStyle(style);
  },

  updateLayerPaint: (layerId, property, value) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    const layer = style.layers.find((l) => l.id === layerId);
    if (!layer) return;
    const paint = (layer.paint ?? {}) as Record<string, unknown>;
    if (value === undefined) {
      delete paint[property];
    } else {
      paint[property] = value;
    }
    layer.paint = paint;
    get().setStyle(style);
  },

  updateLayerLayout: (layerId, property, value) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    const layer = style.layers.find((l) => l.id === layerId);
    if (!layer) return;
    const layout = (layer.layout ?? {}) as Record<string, unknown>;
    if (value === undefined) {
      delete layout[property];
    } else {
      layout[property] = value;
    }
    layer.layout = layout;
    get().setStyle(style);
  },

  updateLayerCommon: (layerId, property, value) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    const layer = style.layers.find((l) => l.id === layerId);
    if (!layer) return;
    layer[property] = value;
    get().setStyle(style);
  },

  addLayer: (layer, position) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    if (position === "bottom") {
      style.layers.unshift(layer);
    } else {
      // "top" or default — renders on top of everything
      style.layers.push(layer);
    }
    get().setStyle(style);
  },

  removeLayer: (layerId) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    style.layers = style.layers.filter((l) => l.id !== layerId);
    set({ selectedLayerId: prev.selectedLayerId === layerId ? null : prev.selectedLayerId });
    get().setStyle(style);
  },

  reorderLayer: (fromIndex, toIndex) => {
    const style = cloneStyle(get().style);
    if (fromIndex < 0 || fromIndex >= style.layers.length) return;
    if (toIndex < 0 || toIndex >= style.layers.length) return;
    const [moved] = style.layers.splice(fromIndex, 1);
    if (moved) style.layers.splice(toIndex, 0, moved);
    get().setStyle(style);
  },

  toggleLayerVisibility: (layerId) => {
    const style = cloneStyle(get().style);
    const layer = style.layers.find((l) => l.id === layerId);
    if (!layer) return;
    const layout = (layer.layout ?? {}) as Record<string, unknown>;
    layout.visibility = layout.visibility === "none" ? "visible" : "none";
    layer.layout = layout;
    get().setStyle(style);
  },

  addSource: (id, source) => {
    const style = cloneStyle(get().style);
    if (!style.sources) style.sources = {};
    style.sources[id] = source;
    get().setStyle(style);
  },

  removeSource: (id) => {
    const prev = get();
    const style = cloneStyle(prev.style);
    if (style.sources) delete style.sources[id];
    set({ selectedSourceId: prev.selectedSourceId === id ? null : prev.selectedSourceId });
    get().setStyle(style);
  },

  updateSource: (id, source) => {
    const style = cloneStyle(get().style);
    if (!style.sources) style.sources = {};
    style.sources[id] = source;
    get().setStyle(style);
  },

  undo: () => {
    const { undoStack, style, redoStack, styleVersion } = get();
    const snapshot = undoStack[undoStack.length - 1];
    if (!snapshot) return;
    try {
      const parsed = JSON.parse(snapshot) as Style;
      set({
        undoStack: undoStack.slice(0, -1),
        redoStack: [...redoStack, JSON.stringify(style)],
        style: parsed,
        styleVersion: styleVersion + 1,
      });
    } catch {
      // Corrupted snapshot — discard it
      set({ undoStack: undoStack.slice(0, -1) });
    }
  },

  redo: () => {
    const { undoStack, style, redoStack, styleVersion } = get();
    const snapshot = redoStack[redoStack.length - 1];
    if (!snapshot) return;
    try {
      const parsed = JSON.parse(snapshot) as Style;
      set({
        redoStack: redoStack.slice(0, -1),
        undoStack: [...undoStack, JSON.stringify(style)],
        style: parsed,
        styleVersion: styleVersion + 1,
      });
    } catch {
      set({ redoStack: redoStack.slice(0, -1) });
    }
  },

  setEditorMode: (mode) => set({ editorMode: mode }),
  setActiveSection: (section) => set({ activeSection: section }),
  setSelectedLayerId: (id) => set({ selectedLayerId: id }),
  setSelectedSourceId: (id) => set({ selectedSourceId: id }),
  setHoveredLayerId: (id) => set({ hoveredLayerId: id }),
  setValidationErrors: (errors) => set({ validationErrors: errors }),
  toggleErrorPanel: () => set((s) => ({ isErrorPanelOpen: !s.isErrorPanelOpen })),

  setViewport: (partial) => set((s) => ({ viewport: { ...s.viewport, ...partial } })),
  setHighlightLayerId: (id) => set({ highlightLayerId: id }),
  toggleComparison: () => set((s) => ({ showComparison: !s.showComparison })),

  loadNewStyle: (style) => {
    const s = style as Style;
    const c = Array.isArray(s.center) && s.center.length >= 2 ? s.center as number[] : [0, 20];
    set({
      style: s,
      originalStyle: cloneStyle(s),
      undoStack: [],
      redoStack: [],
      styleVersion: get().styleVersion + 1,
      selectedLayerId: null,
      selectedSourceId: null,
      validationErrors: [],
      pendingFlyTo: {
        center: [(c[0] as number) ?? 0, (c[1] as number) ?? 20],
        zoom: (typeof s.zoom === "number" ? s.zoom : 2) as number,
        pitch: (typeof s.pitch === "number" ? s.pitch : 45) as number,
        bearing: (typeof s.bearing === "number" ? s.bearing : 0) as number,
      },
    });
  },

  hasUnsavedChanges: () => {
    const { originalStyle, style } = get();
    if (!originalStyle) return false;
    return JSON.stringify(style) !== JSON.stringify(originalStyle);
  },

  runValidation: () => {
    const errors = validateStyleSpec(get().style);
    set({ validationErrors: errors });
  },
}));
